import { CollectionSocials } from '@/components/features/Collection/CollectionSocials';
import { CollectionStats } from '@/components/features/Collection/CollectionStats';
import { Header } from '@/components/features/layout/Header/Header';
import { useStatusBanner } from '@/components/features/layout/StatusBanner/useStatusBanner';
import { BreadcrumbsWithSeo } from '@/components/shared/BreadcrumbsWithSeo';
import { CollectionLink } from '@/components/shared/CollectionLink/CollectionLink';
import { MarkdownRenderer } from '@/components/shared/MarkdownRenderer';
import {
  AspectRatio,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Image,
  keyframes,
  Text,
  useToken,
} from '@chakra-ui/react';
import { ArrowDownIcon, ArrowThickIcon } from '@sphere/icons';
import { BreadcrumbsProps, ButtonLink, MotionBox } from '@sphere/ui';
import { AnimatePresence } from 'framer-motion';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { ActiveItemWrapper } from '../../Editor/components/ActiveItemWrapper';
import { CreatorLink } from './CreatorLink';
import { StaggeredItem } from './StaggeredItem';

interface Props extends PrismaJson.CollectionHeader {
  creatorAddress?: string;
  collectionName?: string | null;
  slug?: string;
  discord?: string;
  twitter?: string;
  externalUrl?: string;
  stats?: { label: string; value: string | number }[];
  isPreview?: boolean;
  activeItem?: string;
  deepdive?: boolean;
  onEnter?: () => void;
  onReturn?: () => void;
  withAspectRatio?: boolean;
  hideReturn?: boolean;
  chainId?: number;
  contractAddress?: string | null;
  breadcrumbs?: BreadcrumbsProps['pages'];
  gameLink?: string;
}

export const HeaderBlock = ({
  creatorAddress,
  collectionName,
  discord,
  twitter,
  externalUrl,
  logo,
  background,
  description,
  stats,
  isPreview,
  activeItem,
  deepdive,
  onEnter,
  onReturn,
  withAspectRatio,
  hideReturn,
  chainId,
  contractAddress,
  breadcrumbs,
  gameLink,
}: Props) => {
  const { height: statusBannerHeight } = useStatusBanner();
  const { t } = useTranslation('collection');
  const bgColor = useToken('colors', 'black');

  const Wrapper = withAspectRatio ? AspectRatio : Box;

  return (
    <Wrapper
      bg="black"
      pos="relative"
      top={0}
      h={withAspectRatio ? 'unset' : '100vh'}
      width="100%"
      overflow="hidden"
      mt={rem(-Header.height - statusBannerHeight)}
      roundedTop={isPreview ? 'radius.32' : undefined}
      {...(withAspectRatio && { ratio: 16 / 9 })}
    >
      <>
        <Box
          pos="absolute"
          inset={0}
          overflow="hidden"
          _after={{
            content: '""',
            pos: 'absolute',
            inset: 0,
            w: '100%',
            h: '100%',
            bg: `linear-gradient(rgba(0, 0, 0, 0.8), transparent 25%, transparent 40%, ${bgColor})`,
          }}
        >
          {background?.src && (
            <Image
              w="100%"
              h="100%"
              objectFit="cover"
              src={background.src}
              alt={t('hero.alt-tags.background', { collection: collectionName })}
              transition="transform 3.3s cubic-bezier(0.8, 0, 0.25, 0.85)"
              transformOrigin="center right"
              transform={`scale(${deepdive ? 1.2 : 1})`}
            />
          )}
        </Box>
        <Box pos="absolute" inset={0}>
          <AnimatePresence>
            {deepdive && (
              <MotionBox
                pos="fixed"
                left={{
                  base: 'space.24',
                  lg: 'space.32',
                  '2xl': 'space.48',
                }}
                top={{
                  base: rem(Header.height + statusBannerHeight),
                }}
                initial={{ translateY: '-20px', opacity: 0 }}
                animate={{ translateY: 0, opacity: 1, transition: { delay: 3 } }}
                exit={{ translateY: '-20px', opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                {...(withAspectRatio && { display: 'none' })}
                zIndex="default"
                display={hideReturn ? 'none' : 'block'}
              >
                <Button
                  mt={{ base: 'space.8', md: 'space.24' }}
                  variant="secondary"
                  onClick={() => onReturn && onReturn()}
                  leftIcon={<ArrowThickIcon boxSize={rem(20)} />}
                  display={hideReturn ? 'none' : 'flex'}
                >
                  {t('hero.back-label')}
                </Button>
              </MotionBox>
            )}
          </AnimatePresence>
          <Container
            variant="breakout"
            pb={{
              base: 'space.24',
              lg: 'space.32',
              '2xl': 'space.48',
            }}
            h="100%"
            pt={{
              base: rem(statusBannerHeight + 80),
              md: rem(Header.height + statusBannerHeight + 24),
            }}
          >
            <AnimatePresence>
              {!deepdive && (
                <Flex h="100%" flexDir="column" justify="space-between" align="flex-start">
                  {breadcrumbs && (
                    <MotionBox
                      initial={{ translateY: '-20px', opacity: 0 }}
                      animate={{ translateY: 0, opacity: 1, transition: { delay: 3 } }}
                      exit={{ translateY: '-20px', opacity: 0 }}
                      transition={{ duration: 0.4, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                    >
                      <BreadcrumbsWithSeo pages={breadcrumbs} />
                    </MotionBox>
                  )}
                  <MotionBox
                    variants={{
                      hidden: {
                        transition: {
                          staggerChildren: 0.05,
                          staggerDirection: 1,
                        },
                      },
                      show: {
                        transition: {
                          staggerChildren: 0.05,
                          delayChildren: 1.45,
                          staggerDirection: 0,
                        },
                      },
                    }}
                    initial="hidden"
                    animate="show"
                    exit="hidden"
                  >
                    <StaggeredItem mb={{ base: 'space.16', md: 'space.24' }}>
                      <Image
                        src={logo?.src}
                        alt={t('hero.alt-tags.logo', { collection: collectionName })}
                        maxH={{ base: rem(80), md: rem(120) }}
                      />
                    </StaggeredItem>

                    <StaggeredItem mb={{ base: 'space.16', md: 'space.24' }} color="white">
                      <Flex align="center" gap="space.14">
                        {chainId && contractAddress && (
                          <CollectionLink
                            h={rem(24)}
                            chainId={chainId}
                            contractAddress={contractAddress}
                          />
                        )}
                        {chainId && contractAddress && creatorAddress && (
                          <Divider h={rem(18)} m={0} orientation="vertical" opacity={1} />
                        )}
                        {creatorAddress && <CreatorLink address={creatorAddress} />}
                      </Flex>
                    </StaggeredItem>
                    <StaggeredItem mb={{ base: 'space.16', md: 'space.32' }}>
                      {stats ? (
                        <CollectionStats
                          items={stats}
                          discord={discord}
                          twitter={twitter}
                          externalUrl={externalUrl}
                          w="unset"
                          display="inline-block"
                        />
                      ) : (
                        <CollectionSocials
                          discord={discord}
                          twitter={twitter}
                          externalUrl={externalUrl}
                        />
                      )}
                    </StaggeredItem>
                    <StaggeredItem mb={{ base: 'space.16', md: 'space.32' }}>
                      <MarkdownRenderer
                        color="white"
                        fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
                        maxWidth={rem(620)}
                        lineHeight={1.5}
                        noOfLines={5}
                        includeParagraphs
                      >
                        {description}
                      </MarkdownRenderer>
                    </StaggeredItem>
                    {onEnter && (
                      <StaggeredItem display="flex" gap="space.16">
                        <Button onClick={onEnter} {...(!gameLink && { variant: 'secondary' })}>
                          {t('hero.cta-label')}
                        </Button>
                        {gameLink && (
                          <ButtonLink variant="secondary" href={gameLink}>
                            {t('hero.game-label')}
                          </ButtonLink>
                        )}
                      </StaggeredItem>
                    )}
                  </MotionBox>
                </Flex>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {deepdive && (
                <Flex h="100%" flexDir="column" justify="space-between">
                  <Flex h="100%" align="center" justify="center">
                    <Box h={rem(200)} overflow="hidden">
                      <MotionBox
                        initial={{ translateY: '200px' }}
                        animate={{ translateY: 0 }}
                        exit={{ translateY: '200px' }}
                        transition={{ duration: 1.3, delay: 1, ease: [0.9, 0, 0.2, 1] }}
                        h="100%"
                        w="100%"
                        display="flex"
                        justifyContent="center"
                      >
                        <ActiveItemWrapper
                          isActive={activeItem === 'logo'}
                          w={logo ? 'auto' : rem(500)}
                          isPreview={isPreview}
                        >
                          {logo?.src && (
                            <Image
                              src={logo.src}
                              alt={t('hero.alt-tags.logo', { collection: collectionName })}
                              h="100%"
                              objectFit="contain"
                            />
                          )}
                        </ActiveItemWrapper>
                      </MotionBox>
                    </Box>
                  </Flex>
                  {creatorAddress && (
                    <MotionBox
                      initial={{ translateY: '60px', opacity: 0 }}
                      animate={{ translateY: 0, opacity: 1 }}
                      exit={{ translateY: '60px', opacity: 0 }}
                      transition={{ duration: 1, delay: 1.6, ease: [0.8, 0, 0.25, 1] }}
                      maxW={rem(384)}
                      pb="space.8"
                      borderBottom="1px solid"
                      borderBottomColor="gray.4"
                    >
                      <Flex align="center" justify="space-between">
                        <CreatorLink address={creatorAddress} />
                        <Flex gap="space.8" align="center" color="white" opacity={0.4}>
                          <Text fontSize="sm">{t('hero.scroll-label')}</Text>
                          <ArrowDownIcon
                            boxSize={rem(16)}
                            animation={`${keyframes`
                        0% {
                          transform: translateY(-1px);

                        }
                        50% {
                          transform: translateY(1px);
                        }
                        100% {
                          transform: translateY(-1px);
                        }
                      `} infinite 2s ease-in-out`}
                          />
                        </Flex>
                      </Flex>
                    </MotionBox>
                  )}
                </Flex>
              )}
            </AnimatePresence>
          </Container>
        </Box>
      </>
    </Wrapper>
  );
};
